// View-model types consumed by the React UI.
// These are the clean, camelCase shapes the UI renders against. The API-boundary
// DTOs live in `@/api/types/api.types`; an adapter in `./adapters.ts` converts
// InvoiceDocument -> Transaction at the edge.

export type TransactionDirection = 'inbound' | 'outbound';
export type TransactionType = 'invoice' | 'order' | 'credit';

// Values mirror the backend vocabulary verbatim (lowercase, single-l canceled,
// snake_case extended states) so the enum doubles as the over-the-wire shape.
export enum DocumentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SKIPPED = 'skipped',
  CANCELED = 'canceled',
  RETRY = 'retry',
  MISSING_DATA = 'missing_data',
  TRANSFER_FAILED = 'transfer_failed',
  IMS_UPDATE_FAILED = 'ims_update_failed',
  PREVIOUSLY_PROCESSED = 'previously_processed',
}

export type TransactionStatus = DocumentStatus;

export interface Address {
  name: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface TransactionLine {
  line: number;
  description: string;
  qty: number;
  unit: string;
  unitPrice: number;
  total: number;
  discountPct?: number;
  discount?: number;
  amount: number;
}

export interface TransactionEvent {
  id: string;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'success';
  event: string;
  details?: string;
}

export interface Transaction {
  id: string;
  number: string;
  // Routing keys needed to round-trip through PUT /invoices.
  location: string;
  groupDb: string;
  // Sort key on the invoice DynamoDB table — must be sent unchanged on update.
  modified: string;
  // Raw legacy payload — required by the current update contract, which
  // demands the full invoiceData blob even for status-only changes.
  rawInvoiceData?: object;
  direction: TransactionDirection;
  status: TransactionStatus;
  type: TransactionType;
  docType: string;
  tradingPartner: string;
  tradingPartnerId: string;
  date: string;
  amount?: number;
  currency?: string;
  poNumber?: string;
  vendorCode?: string;
  reasonForFail?: string;
  billFrom?: Address;
  billTo?: Address;
  shipTo?: Address;
  remitTo?: Address;
  lines?: TransactionLine[];
  supplierTaxNumber?: string;
  buyerTaxNumber?: string;
  billOfLading?: string;
  glAccount?: string;
  tungstenTransactionId?: string;
  events?: TransactionEvent[];
}

export interface TradingPartner {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  health: 'healthy' | 'degraded' | 'down';
  successRate: number;
  totalTransactions: number;
  lastTransactionAt?: string;
  supportedDocTypes: string[];
}

export interface KpiSnapshot {
  todayVolume: number;
  todayDeltaPct: number;
  failureRate: number;
  failureDeltaPct: number;
  queueDepth: number;
  avgLatencyMs: number;
}

export interface VolumePoint {
  timestamp: string;
  inbound: number;
  outbound: number;
  failed: number;
}

export interface StatusBreakdown {
  complete: number;
  pending: number;
  failed: number;
}

// A status filter and a by-number lookup are different backend access patterns
// and cannot be combined, so the mode is explicit rather than inferred from
// whichever fields happen to be populated.
export type TransactionSearchMode = 'status' | 'name';

export interface TransactionQuery {
  mode: TransactionSearchMode;
  // In 'name' mode `number`, `location` and `groupDb` compose into the composite
  // document id `{number}#{location}#{groupDb}` and all three are required.
  // In 'status' mode they are independent filters.
  number?: string;
  location?: string;
  groupDb?: string;
  docType?: string;
  status?: TransactionStatus;
  direction?: TransactionDirection;
  type?: TransactionType;
  partnerId?: string;
  dateFrom?: string;
  dateTo?: string;
  cursor?: string;
  limit?: number;
}

export interface Paged<T> {
  items: T[];
  nextCursor?: string;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Transmission logs (schedule providers)
//
// Square D and Siemens don't push discrete documents — a scheduled run collects
// POS sales or an inventory snapshot, splits it into batches and sends each one.
// The log records one row per transmitted batch, so a run that sent three
// batches leaves three rows sharing a `runTimestamp`.
// ---------------------------------------------------------------------------

/**
 * Normalised delivery result. Three backend flows write three different
 * vocabularies into the raw `status` column, so the API reports this alongside
 * it — filter and branch on `outcome`, never on `status`.
 *
 * An unrecognised status normalises to `pending`, never `sent`: not knowing
 * what a status means is not evidence a payload reached the partner.
 */
export type TransmissionOutcome = 'sent' | 'failed' | 'pending';

/** The two transmission streams, each backed by its own endpoint and table. */
export type TransmissionKind = 'pos' | 'inventory';

export interface TransmissionLog {
  /** Composite row id — opaque, only ever handed back to the API. */
  id: string;
  integration?: string;
  vendorId: string;
  /** Raw status as the transmitting flow stored it. Free text — display only. */
  status: string;
  outcome: TransmissionOutcome;
  /** When the transmission was attempted. Sort key: always newest first. */
  modified: string;
  /** When the row itself was written. */
  timestamp?: string;
  recordCount?: number;
  batchIndex?: number;
  errorMessage?: string;
  /** Whether a payload was archived — gates the download action. */
  hasDocument: boolean;

  // POS-only. An inventory report is a stock snapshot taken at a moment, so it
  // has no run window; a POS run reports on a date range of documents.
  runTimestamp?: string;
  trigger?: string;
  startDate?: string;
  endDate?: string;
}

/** A time-limited link to the payload a transmission archived. */
export interface TransmissionDownload {
  id: string;
  integration?: string;
  status: string;
  outcome: TransmissionOutcome;
  /** Presigned S3 URL. Expires in ~5 minutes — re-request rather than cache. */
  url: string;
  expiresAt: string;
  filename: string;
  contentType?: string;
  sizeBytes?: number;
}

export interface TransmissionQuery {
  kind: TransmissionKind;
  /**
   * Wire name of the integration to read, e.g. `siemens-pos`. Required by the
   * backend — it's the table's partition key, which is also why one request
   * can only ever cover a single integration.
   */
  integration: string;
  outcome?: TransmissionOutcome;
  /** Inclusive bounds on `modified`, as `YYYY-MM-DD` calendar days. */
  dateFrom?: string;
  dateTo?: string;
  cursor?: string;
  limit?: number;
}

/**
 * A request to replay one POS integration's transmission over a date range.
 *
 * The range is held as `YYYY-MM-DD` calendar days — the shape a date input
 * produces — and widened into the inclusive UTC instants the backend wants at
 * the API edge.
 *
 * POS only, and not by omission: an inventory report transmits the current
 * stock table rather than a window of past documents, so there is nothing for
 * a date range to select.
 */
export interface TransmissionResendRequest {
  /** Wire name of the POS integration, e.g. `siemens-pos`. */
  integration: string;
  startDate: string;
  endDate: string;
}
